import * as THREE from 'three';

import { Ball } from './Ball';
import { PlayerState, Team } from '@server/rooms/schema/BasketballRoomState';
import { GLBAvatarPlayer } from './GLBAvatarPlayer';
import { NetworkManager } from '../managers/NetworkManager';
import { Debug, LogCategory } from '@shared/Debug';
import { disposeObject } from './ModelLoader';

export class Player {
  private mesh: THREE.Group; // Child group for animation-driven transformations
  private position: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private velocity: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private nameLabel: THREE.Sprite | null = null;
  private localPlayerRing: THREE.Mesh | null = null; // Ring indicator for local player

  private direction: THREE.Vector3 = new THREE.Vector3(0, 0, -1); // Looking direction

  private name: string;
  private id: string; // Player ID (same as session ID)
  private team: Team;
  private hasBallValue: boolean = false;
  
  private state: PlayerState = PlayerState.IDLE;
  private pendingState: PlayerState = PlayerState.IDLE;
  private animationTime: number = 0;
  private stateChangeTime: number = 0;
  
  private _isLocalPlayer: boolean = false;  
  
  // Ball representation for the player
  private ballMesh: THREE.Mesh | null = null;
  private ball: Ball | null = null; // Reference to the ball object
  private onPlayerMoveCallbacks: ((player: Player) => void)[] = [];
  private onPlayerShotCallbacks: ((velocityX: number, velocityY: number, velocityZ: number) => void)[] = [];
  private lastUpdateTime: number = 0;
  private onShotReleased: (() => void) | null = null;
  private shotReleased: boolean = false;
  
  // GLB Avatar
  private glbAvatar: GLBAvatarPlayer | null = null;
  private glbAvatarLoaded: boolean = false;
  private isDisposed: boolean = false;

  private networkManager: NetworkManager | null = null;
  
  constructor(
    name: string, 
    id: string,
    team: Team,
    isLocalPlayer: boolean, 
    networkManager?: NetworkManager,
    modelPath?: string,
    animationFiles?: string[]
  ) {
    this.name = name;
    this.id = id;
    this.team = team;
    this._isLocalPlayer = isLocalPlayer;
    this.networkManager = networkManager;

    
    // Create a basic player mesh group (child of parent)
    this.mesh = new THREE.Group();
    this.mesh.name = `player-${name}-mesh`;
    
    // Create name label
    this.createNameLabel();
    
    // Create local player ring for all players
    this.createLocalPlayerRing();
    
    // Only support GLB/GLTF models
    if (modelPath) {
      const extension = modelPath.toLowerCase().split('.').pop();
      if (extension === 'glb' || extension === 'gltf') {
        this.glbAvatar = new GLBAvatarPlayer(modelPath, animationFiles);
      } else {
        console.warn(`Unsupported model format: ${extension}, cannot create player`);
      }
    } else {
      console.warn(`No model path provided for player ${name}`);
    }
  }
  
  /**
   * Loads the GLB avatar and attaches it to the player
   */
  private async loadCustomAvatar(): Promise<void> {
    let model: THREE.Group | null = null;

    try {
      if (this.glbAvatar) {
        Debug.info(`[${this.name}] Starting GLB avatar load`, LogCategory.PLAYER);
        model = await this.glbAvatar.getModel();
        this.glbAvatarLoaded = true;
      }

      if (!model) {
        Debug.error(`[${this.name}] Failed to get GLB model`, LogCategory.PLAYER);
        return;
      }

      Debug.info(`[${this.name}] GLB model loaded successfully:\n ${JSON.stringify(model)}`, LogCategory.PLAYER);
      
      
      
      // Ensure the model and all its parts are visible
      model.traverse(child => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          child.visible = true;
          Debug.info(`[${this.name}] Configured mesh: ${child.name || 'unnamed'}, visible: ${child.visible}`, LogCategory.PLAYER);
        }
      });
      
      // Add the model to the player mesh
      this.mesh.add(model);

      // Force matrix updates
      model.updateMatrix();
      this.mesh.updateMatrix();           
      
      // Log success with detailed information
      Debug.info(`[${this.name}] GLB model successfully attached:`, LogCategory.PLAYER);
      
      Debug.info(`modelPosition: ${JSON.stringify(model.position)}`, LogCategory.PLAYER);
      Debug.info(`modelRotation: ${JSON.stringify(model.rotation)}`, LogCategory.PLAYER);
      Debug.info(`modelScale: ${JSON.stringify(model.scale)}`, LogCategory.PLAYER);
      Debug.info(`modelVisible: ${JSON.stringify(model.visible)}`, LogCategory.PLAYER);
      Debug.info(`meshPosition: ${JSON.stringify(this.mesh.position)}`, LogCategory.PLAYER);
      Debug.info(`meshRotation: ${JSON.stringify(this.mesh.rotation)}`, LogCategory.PLAYER);
      Debug.info(`meshScale: ${JSON.stringify(this.mesh.scale)}`, LogCategory.PLAYER);
      Debug.info(`meshVisible: ${JSON.stringify(this.mesh.visible)}`, LogCategory.PLAYER);
      
      // Add debug visualization to see the orientation
      this.toggleDebugVisualization(false); // Enable debug visualization temporarily
      
      // Set initial state to idle to start animating
      this.setPendingState(PlayerState.IDLE);
      
      // Log the model hierarchy after attachment
      //console.log(`[${this.name}] Final model hierarchy:`);
      //this.logObjectHierarchy(this.mesh);
      
      // Force a position update to ensure the model is in the correct position
      this.setPosition(this.position.x, this.position.y, this.position.z);
    } catch (error) {
      Debug.error(`[${this.name}] Failed to load GLB avatar: ${error}`, LogCategory.PLAYER);
    }
  }
  
  /**
   * Helper method to log the hierarchy of an object
   */
  private logObjectHierarchy(object: THREE.Object3D, indent: string = ''): void {
    Debug.info(`${indent}${object.name || 'unnamed'} (${object.type}) - rotation: ${object.rotation.y.toFixed(2)}, position: [${object.position.x.toFixed(2)}, ${object.position.y.toFixed(2)}, ${object.position.z.toFixed(2)}]`, LogCategory.PLAYER);
    object.children.forEach(child => {
      this.logObjectHierarchy(child, indent + '  ');
    });
  }
  
  /**
   * Creates a name label sprite that always faces the camera
   */
  private createNameLabel(): void {
    // Create canvas for text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    // Set canvas size
    canvas.width = 256;
    canvas.height = 64;

    // Draw background with team color
    const backgroundColor = this.team === Team.A ? 'rgba(255, 0, 0, 0.5)' : 'rgba(0, 0, 255, 0.5)';
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Draw text
    context.font = 'bold 32px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = '#ffffff';
    context.fillText(this.name, canvas.width / 2, canvas.height / 2);

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true,
      depthTest: false // Ensure label is always visible
    });

    // Create sprite
    this.nameLabel = new THREE.Sprite(material);
    this.nameLabel.scale.set(2, 0.5, 1); // Adjust size as needed
    this.nameLabel.position.set(0, 2.5, 0); // Position higher above player's head

    // Add to player mesh
    this.mesh.add(this.nameLabel);
  }
  
  /**
   * Creates a ring indicator around the local player
   */
  private createLocalPlayerRing(): void {
    // Create a ring geometry
    const ringGeometry = new THREE.RingGeometry(0.8, 1.0, 32);
    
    // Create a material that emits light and is semi-transparent
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: this.team === Team.A ? 'rgba(255, 0, 0, 0.5)' : 'rgba(0, 0, 255, 0.5)',
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
      depthWrite: false, // Don't write to depth buffer
      depthTest: true // Still test against depth buffer
    });
    
    // Create the ring mesh
    this.localPlayerRing = new THREE.Mesh(ringGeometry, ringMaterial);
    
    // Rotate ring to lay flat on the ground
    this.localPlayerRing.rotation.x = -Math.PI / 2;
    
    // Position slightly higher above ground to ensure it's above court lines
    this.localPlayerRing.position.y = 0.02;
    
    // Initially hide the ring for non-local players
    this.localPlayerRing.visible = this._isLocalPlayer;
    
    // Add to player mesh
    this.mesh.add(this.localPlayerRing);
  }
  
  /**
   * Enforces position constraints to keep the player within valid bounds
   * Currently ensures Y never goes below 0
   */
  private enforcePositionConstraints(): void {
    // Ensure player never goes below ground level
    if (this.position.y < 0) {
      this.position.y = 0;
      this.mesh.position.y = 0;
    }
  }
  
  /**
   * Updates the player for the current frame
   */
  public update(): void {
    const now = performance.now();
    const deltaTime = (now - this.lastUpdateTime) / 1000;
    this.lastUpdateTime = now;
    
    // Skip if the time delta is too large (e.g. after tab switch)
    if (deltaTime > 0.5) return;
    
    // Animation updates
    this.animationTime += deltaTime;
        
    
    if (this.glbAvatar) {
      // Update GLB animations
      this.glbAvatar.update(deltaTime);
    }

    // Update ball position if we have the ball
    if (this.hasBallValue && this.ballMesh) {
      this.updateBallPosition();
    }

    // Enforce position constraints
    this.enforcePositionConstraints();

    // Update name label position to stay above head
    if (this.nameLabel) {
      this.nameLabel.position.y = 2.5; // Keep label higher above head
    }

    // Rotate the local player ring if it exists
    if (this.localPlayerRing) {
      this.localPlayerRing.rotation.z += deltaTime; // Rotate the ring over time
    }
  }
  

  public setPosition(x: number, y: number, z: number): void {
    // Ensure y position is never below 0
    const constrainedY = Math.max(0, y);
    this.position.set(x, constrainedY, z);
    this.mesh.position.copy(this.position);
  }
  
  public getPosition(): THREE.Vector3 {
    return this.position.clone();
  }

  public setRotation(rotationX: number, rotationY: number, rotationZ: number): void {
    this.mesh.rotation.set(rotationX, rotationY, rotationZ);
  }

  public setRotationFromQuarternian(quaternion: THREE.Quaternion): void {
    this.mesh.rotation.setFromQuaternion(quaternion);
  }
  
  public getRotation(): THREE.Euler {
    return this.mesh.rotation;
  }

  public move(velocityX: number, velocityZ: number): void {
    // Don't move while shooting
    if (this.state === PlayerState.SHOOTING) {
        return;
    }

    this.setPendingState(PlayerState.MOVING);

    // Set velocity (keep Y at 0 since we're moving on the court plane)
    this.velocity.set(velocityX, 0, velocityZ);
    
    // Update direction based on movement
    if (velocityX !== 0 || velocityZ !== 0) {
        this.direction.set(velocityX, 0, velocityZ).normalize();
    }        
    
    // Notify movement callbacks
    this.onPlayerMoveCallbacks.forEach(callback => callback(this));
  }

  public shoot(targetPosition: THREE.Vector3): THREE.Vector3 {   
    Debug.info('shoot', LogCategory.PLAYER);
    
    // Pre-position the ball for shooting to prevent position glitches
    if (this.hasBallValue && this.ballMesh) {
        Debug.info('Pre-positioning ball for shot', LogCategory.PLAYER);
        this.updateBallPosition();
    }
    
    // Start shooting animation
    this.setPendingState(PlayerState.SHOOTING);
    this.stateChangeTime = this.animationTime;

    //this.mesh.lookAt(targetPosition.x, 0, targetPosition.z);
    
    // Face the target
    const direction = new THREE.Vector3()
    .subVectors(targetPosition, this.position)
    .normalize();
    direction.y = 0; // Keep y level
    this.direction.copy(direction);

    // -------- PHYSICS-BASED PERFECT SHOOTING --------
    // Calculate the exact velocity needed to hit the target using projectile motion equations
    const distanceToBasket = this.position.distanceTo(targetPosition);
    Debug.info(`Distance to basket: ${distanceToBasket.toFixed(2)}`, LogCategory.PLAYER);
    
    // Get the horizontal and vertical distances
    const horizontalDistance = Math.sqrt(
      Math.pow(targetPosition.x - this.position.x, 2) + 
      Math.pow(targetPosition.z - this.position.z, 2)
    );
    
    // Target is the rim, which is 3.05m (10ft) high
    const releaseOffset = 0.5;
    const releasePosition = new THREE.Vector3(
      this.position.x + direction.x * releaseOffset, 
      this.position.y + 1.7, // Match server-side face height
      this.position.z + direction.z * releaseOffset
    );
    const targetHeight = targetPosition.y;
    const releaseHeight = releasePosition.y;
    const heightDifference = targetHeight - releaseHeight;
    
    // Gravity constant (matches the server's gravity setting)
    const gravity = 9.81;
    
    // We need to solve the projectile motion equations for initial velocity
    // For a perfect swish, we want the ball to be descending when it reaches the hoop
    
    // Adjusted time of flight for a flatter trajectory
    // Reducing base value to create a flatter arc
    const desiredTimeOfFlight = 0.6 + (distanceToBasket * 0.12);
    
    // Calculate the initial velocity components using the projectile motion equation:
    // Horizontal: v_x = distance / time
    const horizontalVelocity = horizontalDistance / desiredTimeOfFlight;
    
    // Vertical: Use the equation h = vt - 0.5gt² solved for v
    // We need to ensure the ball reaches the target height after exactly our desired time
    // Reduced vertical boost for flatter trajectory
    const verticalVelocity = (heightDifference / desiredTimeOfFlight) + (gravity * desiredTimeOfFlight / 2) + 0.8;
    
    // Apply a small power multiplier to ensure shots reach their target
    // Maintaining the horizontal power but not increasing vertical as much
    const powerMultiplier = 1.15; // Reduced from 1.2 to account for closer release point
    
    // Scale horizontal velocity components according to direction with power multiplier
    const velocityX = direction.x * horizontalVelocity * powerMultiplier;
    const velocityZ = direction.z * horizontalVelocity * powerMultiplier;
    
    Debug.info(`Physics-calculated velocity: vx=${velocityX.toFixed(2)}, vy=${verticalVelocity.toFixed(2)}, vz=${velocityZ.toFixed(2)}`, LogCategory.PLAYER);
    Debug.info(`Time of flight: ${desiredTimeOfFlight.toFixed(2)}s`, LogCategory.PLAYER);
    
    // Double-check our trajectory math with prediction of landing spot
    // Using the projectile motion equation: x = v_x * t, y = v_y * t - 0.5 * g * t²
    const predictionTime = desiredTimeOfFlight;
    const predictedX = releasePosition.x + velocityX * predictionTime;
    const predictedY = releasePosition.y + verticalVelocity * predictionTime - 
                     (0.5 * gravity * predictionTime * predictionTime);
    const predictedZ = releasePosition.z + velocityZ * predictionTime;
    
    Debug.info(`Predicted landing position: x=${predictedX.toFixed(2)}, y=${predictedY.toFixed(2)}, z=${predictedZ.toFixed(2)}`, LogCategory.PLAYER);
    Debug.info(`Target position: x=${targetPosition.x.toFixed(2)}, y=${targetPosition.y.toFixed(2)}, z=${targetPosition.z.toFixed(2)}`, LogCategory.PLAYER);
    
    this.shotReleased = false;
    
    this.onShotReleased = () => {
        this.shotReleased = true;
        Debug.info('Shot released!', LogCategory.PLAYER);
        Debug.info(`velocityX: ${velocityX.toFixed(2)}, velocityY: ${verticalVelocity.toFixed(2)}, velocityZ: ${velocityZ.toFixed(2)}`, LogCategory.PLAYER);
        this.onPlayerShotCallbacks.forEach(
            callback => callback(velocityX, verticalVelocity, velocityZ));
    };

    return releasePosition;
  }
  
  public pass(targetPosition: THREE.Vector3): void {
    // Face the target
    const direction = new THREE.Vector3()
      .subVectors(targetPosition, this.position)
      .normalize();
    direction.y = 0; // Keep y level
    this.direction.copy(direction);
    
    // Start passing animation
    this.setPendingState(PlayerState.PASSING);
    this.stateChangeTime = this.animationTime;
  }
  
  public block(): void {
    // Start blocking/jumping animation
    this.setPendingState(PlayerState.BLOCKING);
    this.stateChangeTime = this.animationTime;
  }
  
  public celebrate(): void {
    this.setPendingState(PlayerState.CELEBRATING);
    this.stateChangeTime = this.animationTime;
  }
  
  public defeat(): void {
    this.setPendingState(PlayerState.DEFEATED);
    this.stateChangeTime = this.animationTime;
  }
  
  public setHasBall(hasBall: boolean, ball: Ball | null = null): void {
    // Update our tracking properties - note that hasBallValue should already be set
    // by PlayerManager.setPlayerHasBall via setHasBallValue before this method is called
    // We'll still set it here for backward compatibility
    this.hasBallValue = hasBall;
    this.ball = hasBall ? ball : null;
    
    // Skip if no ball provided
    if (!ball) {
        Debug.info(`Player ${this.name}: No ball provided in setHasBall, skipping visual update`, LogCategory.PLAYER);
        return;
    }
    
    // Update the visual representation
    if (hasBall) {
        // Store reference to the ball mesh
        this.ballMesh = ball.getMesh();
        
        // Make sure the ball is visible
        if (this.ballMesh) {
            this.ballMesh.visible = true;
            
            // Ensure proper attachment of the ball to the player
            try {
                // First, remove ball from its current parent if it has one
                if (this.ballMesh.parent) {
                    this.ballMesh.parent.remove(this.ballMesh);
                    Debug.info(`Ball removed from previous parent before attaching to player ${this.name}`, LogCategory.PLAYER);
                }
                
                // Add to player's mesh
                this.mesh.add(this.ballMesh);
                
                // Position the ball based on player state
                this.updateBallPosition();
                
                // Reset any rotation or scale that might be affecting the ball
                this.ballMesh.rotation.set(0, 0, 0);
                
                // Force matrix update to ensure proper positioning
                this.ballMesh.updateMatrix();
                this.mesh.updateMatrixWorld(true);
                
                Debug.info(`Ball properly attached to player ${this.name}'s mesh at position: ${JSON.stringify(this.ballMesh.position)}`, LogCategory.PLAYER);
            } catch (error) {
                Debug.error(`Error attaching ball to player ${this.name}: ${error}`, LogCategory.PLAYER);
            }
        } else {
            Debug.warn(`Ball mesh is null when trying to give ball to player ${this.name}`, LogCategory.PLAYER);
        }
    } else if (this.ballMesh) {
        // If the player no longer has the ball, remove it from the player's mesh
        try {
            if (this.ballMesh.parent === this.mesh) {
                // Get world position before removing to maintain continuity
                const worldPosition = new THREE.Vector3();
                this.ballMesh.getWorldPosition(worldPosition);
                
                // Remove from player
                this.mesh.remove(this.ballMesh);
                
                Debug.info(`Ball removed from player ${this.name}'s mesh at world position: ${JSON.stringify(worldPosition)}`, LogCategory.PLAYER);
            } else {
                Debug.info(`Ball is not attached to player ${this.name}'s mesh, no need to remove`, LogCategory.PLAYER);
            }
            
            // Clear the ball mesh reference
            this.ballMesh = null;
        } catch (error) {
            Debug.error(`Error removing ball from player ${this.name}: ${error}`, LogCategory.PLAYER);
        }
    }
  }
  
  /**
   * Updates the ball position based on the player's current state
   */
  private updateBallPosition(): void {
    if (!this.ballMesh) return;
    
    // Store previous position for debugging
    const prevPosition = this.ballMesh.position.clone();
    
    // Default position - holding ball at right side
    let x = -0.25;
    let y = 1.0;
    let z = 0.25;
    
    // Adjust position based on player state
    switch (this.state) {
      case PlayerState.IDLE:
        // Standard holding position
        x = -0.25;
        y = 1.0;
        z = 0.25;
        break;
        
      case PlayerState.MOVING:
        // Lower position for dribbling
        x = -0.3;
        y = 1.0;
        z = 0.25;
        break;
        
      case PlayerState.SHOOTING:
        // Position for shooting - above head
        x = -0.25;
        y = 1.8;
        z = 0.25;
        break;
        
      case PlayerState.PASSING:
        // Position for passing - out front
        x = -0.25;
        y = 1.0;
        z = 0;
        break;
        
      default:
        // Default position
        x = -0.25;
        y = 1.0;
        z = 0.25;
    }
    
    // Apply the calculated position
    this.ballMesh.position.set(x, y, z);
    
    // Force matrix update to ensure the ball is properly positioned
    this.ballMesh.updateMatrix();
   
  }

  public hasBall(): boolean {
    return this.hasBallValue;
  }

  /**
   * Sets the hasBall value without affecting the ball mesh
   * This is used for internal state tracking only
   */
  public setHasBallValue(value: boolean): void {
    this.hasBallValue = value;
  }

  public setPendingState(state: PlayerState): void {

    if (this.pendingState !== state && this.state !== state) {
    this.pendingState = state;

      Debug.info(`Setting pending state to ${state}`, LogCategory.PLAYER);
      if (this.networkManager) {
        this.networkManager.sendMessage('setPlayerState', { playerState: state });
      }
    }
  }
  
  /**
   * Set the player state and update animations accordingly
   */
  public setState(state: PlayerState): void {
    // Only log state changes
    if (this.state !== state) {
        Debug.info(`Attempting to set state to ${state} from ${this.state}`, LogCategory.PLAYER);
    
        // If we had the ball and we're changing to a shooting state,
        // make sure we update the ball position before the animation starts
        if (this.hasBallValue && this.ballMesh && state === PlayerState.SHOOTING) {
            Debug.info('Pre-positioning ball for shooting animation', LogCategory.PLAYER);
            this.updateBallPosition();
        }
    
        this.state = state;
        this.stateChangeTime = performance.now();

        // Play appropriate GLB animation based on state
        if (this.glbAvatar) {
          switch (state) {
              case PlayerState.IDLE:
                Debug.info('Setting GLB avatar to idle animation', LogCategory.PLAYER);
                this.glbAvatar.playAnimation('idle', true);
                break;
              case PlayerState.MOVING:
                Debug.info('Setting GLB avatar to run animation', LogCategory.PLAYER);
                this.glbAvatar.playAnimation('run_dribble', true);
                break;
              case PlayerState.SHOOTING:
                Debug.info('Setting GLB avatar to shoot animation', LogCategory.PLAYER);
                this.glbAvatar.playAnimation('shoot', false);
                // When shooting, make sure the ball is immediately visible in the right position
                if (this.hasBallValue && this.ballMesh) {
                   this.updateBallPosition();
                }
                setTimeout(() => {
                    if (this.onShotReleased) {
                      this.onShotReleased();
                      this.shotReleased = true;
                    }
                  }, 300);
                  setTimeout(() => {
                    this.setPendingState(PlayerState.IDLE);
                  }, 700);
                break;
              case PlayerState.PASSING:
                Debug.info('Setting GLB avatar to pass animation', LogCategory.PLAYER);
                this.glbAvatar.playAnimation('pass', false);
                
                // Add timeout to reset state after pass animation completes
                setTimeout(() => {
                  this.setPendingState(PlayerState.IDLE);
                }, 500); // Adjust timing based on pass animation length
                break;
              case PlayerState.BLOCKING:
                Debug.info('Setting GLB avatar to block animation', LogCategory.PLAYER);
                this.glbAvatar.playAnimation('block', false);
                break;
              case PlayerState.CELEBRATING:
                Debug.info('Setting GLB avatar to celebrate animation', LogCategory.PLAYER);
                this.glbAvatar.playAnimation('celebrate', true);
                break;
              case PlayerState.DEFEATED:
                Debug.info('Setting GLB avatar to defeat animation', LogCategory.PLAYER);
                this.glbAvatar.playAnimation('defeat', true);
                break;
              default:
                Debug.warn(`Unknown state: ${state}, defaulting to idle`, LogCategory.PLAYER);
                this.glbAvatar.playAnimation('idle', true);
          }
        }
        
        // Update ball position immediately after state change
        if (this.hasBallValue && this.ballMesh) {
            this.updateBallPosition();
        }
    }
  }

  public getVelocity(): THREE.Vector3 {
    return this.velocity;
  }
  
  public getState(): PlayerState {
    return this.state;
  }
  
  public isOpponent(player: Player): boolean {
    return this.team !== player.getTeam();
  }
  
  public getName(): string {
    return this.name;
  }
  
  public getTeam(): Team {
    return this.team;
  }

  public isLocalPlayer(): boolean {
    return this._isLocalPlayer;
  }
  
  /**
   * Get the player's unique ID
   */
  public getId(): string {
    return this.id;
  }
  
  /**
   * Properly disposes of all resources used by this player
   */
  public dispose(): void {
    if (this.isDisposed) {
      return; // Prevent double disposal
    }
    
    Debug.info(`Disposing player: ${this.name}`, LogCategory.PLAYER);
    
    // Clean up event listeners and callbacks
    this.onPlayerMoveCallbacks = [];
    this.onPlayerShotCallbacks = [];
    this.onShotReleased = null;
    
    // Clean up ball reference (don't dispose - it's managed elsewhere)
    this.ball = null;
    
    // Dispose of ball mesh if it exists
    if (this.ballMesh) {
      disposeObject(this.ballMesh);
      this.ballMesh = null;
    }
    
    // Dispose of name label if it exists
    if (this.nameLabel) {
      if (this.nameLabel.material) {
        if (Array.isArray(this.nameLabel.material)) {
          this.nameLabel.material.forEach(m => m.dispose());
        } else {
          this.nameLabel.material.dispose();
        }
      }
      this.nameLabel = null;
    }
    
    // Dispose of ring indicator if it exists
    if (this.localPlayerRing) {
      disposeObject(this.localPlayerRing);
      this.localPlayerRing = null;
    }
    
    // Dispose of GLB avatar if it exists
    if (this.glbAvatar) {
      this.glbAvatar.dispose();
      this.glbAvatar = null;
    }
    
    // Dispose of main mesh
    disposeObject(this.mesh);
    
    // Mark as disposed
    this.isDisposed = true;
  }
  
  /**
   * Gets the player's mesh
   */
  public async getMesh(): Promise<THREE.Group> {
    // If using GLB avatar and it hasn't been loaded yet, load it now
    if (!this.glbAvatarLoaded && !this.isDisposed) {
      await this.loadCustomAvatar();      
    }
    
    // Ensure position constraints are respected after loading
    this.enforcePositionConstraints();
    
    return this.mesh;
  }
  
  /**
   * Check if this player is using a GLB avatar
   */
  public isUsingCustomAvatar(): boolean {
    return this.glbAvatar !== null;
  }
  
  /**
   * Get the GLB avatar player if available
   */
  public getCustomAvatar(): GLBAvatarPlayer | null {
    return this.glbAvatar;
  }

  public onPlayerMove(callback: (player: Player) => void): void {
    this.onPlayerMoveCallbacks.push(callback);
  }

  public removeOnPlayerMoved(callback: (player: Player) => void): void {
    this.onPlayerMoveCallbacks = this.onPlayerMoveCallbacks.filter(cb => cb !== callback);
  }

  public onPlayerShot(callback: (velocityX: number, velocityY: number, velocityZ: number) => void): void {
    this.onPlayerShotCallbacks.push(callback);
  }

  public removeOnPlayerShot(callback: (velocityX: number, velocityY: number, velocityZ: number) => void): void {
    this.onPlayerShotCallbacks = this.onPlayerShotCallbacks.filter(cb => cb !== callback);
  }
  
  /**
   * Toggle debug visualization for the GLB model
   * @param enable Whether to enable debug visualization
   */
  public toggleDebugVisualization(enable: boolean): void {
    if (this.glbAvatar) {
      // Create a helper to visualize the player position
      if (enable) {
        // Create axes helper
        const axesHelper = new THREE.AxesHelper(1);
        axesHelper.name = 'debug-axes';
        this.mesh.add(axesHelper);
        
        // Create a sphere to mark the center point
        const centerSphere = new THREE.Mesh(
          new THREE.SphereGeometry(0.1, 8, 8),
          new THREE.MeshBasicMaterial({ color: 0xff0000, wireframe: true })
        );
        centerSphere.name = 'debug-center';
        this.mesh.add(centerSphere);
        
        Debug.info('Debug visualization enabled for player', LogCategory.PLAYER);
      } else {
        // Remove debug visualization
        const axesHelper = this.mesh.getObjectByName('debug-axes');
        if (axesHelper) this.mesh.remove(axesHelper);
        
        const centerSphere = this.mesh.getObjectByName('debug-center');
        if (centerSphere) this.mesh.remove(centerSphere);
        
        Debug.info('Debug visualization disabled for player', LogCategory.PLAYER);
      }
    }
  }

  /**
   * Debug method to directly play GLB animations by name
   * This can be called from the browser console to test animations
   * @param animationName Name of the animation to play
   * @param loop Whether to loop the animation
   */
  public debugPlayAnimation(animationName: string, loop: boolean = true): void {
    if (this.glbAvatar) {
      Debug.info(`Debug: Playing animation "${animationName}" with loop=${loop}`, LogCategory.PLAYER);
      this.glbAvatar.playAnimation(animationName, loop);
    } else {
      Debug.warn('Debug: GLB avatar not available', LogCategory.PLAYER);
    }
  }

  /**
   * Debug method to list all available animations
   * This can be called from the browser console to see what animations are available
   */
  public debugListAnimations(): string[] {
    if (this.glbAvatar) {
      const animations = this.glbAvatar.getAvailableAnimations();
      Debug.info(`Available animations: ${JSON.stringify(animations)}`, LogCategory.PLAYER);
      return animations;
    } else {
      Debug.warn('Debug: GLB avatar not available', LogCategory.PLAYER);
      return [];
    }
  }

  /**
   * Debug helper to check custom model structure and find any issues with the model attachment
   */
  public debugInspectCustomModel(): void {
    Debug.info('Debug Custom Model Inspection', LogCategory.PLAYER);
    
    Debug.info(`Player position: ${JSON.stringify(this.position)}`, LogCategory.PLAYER);
    Debug.info(`Player mesh position: ${JSON.stringify(this.mesh.position)}`, LogCategory.PLAYER);
    Debug.info(`Player mesh rotation: ${JSON.stringify(this.mesh.rotation)}`, LogCategory.PLAYER);
    
    // Check if position constraints are violated
    if (this.position.y < 0) {
      Debug.warn('Player Y position below ground level! Fixing...', LogCategory.PLAYER);
      this.position.y = 0;
      this.mesh.position.y = 0;
    }
    
    // Check if custom avatar is being used
    if (!this.isUsingCustomAvatar()) {
      console.error('No custom avatar is being used');
      console.groupEnd();
      return;
    }
    
    // Find the model in the player mesh
    const model = this.mesh.getObjectByName('glb-outer-container');
    if (!model) {
      Debug.error('GLB model not found in player mesh', LogCategory.PLAYER);
      return;
    }
    
    Debug.info(`Model found: ${JSON.stringify(model)}`, LogCategory.PLAYER);
    Debug.info(`Model position: ${JSON.stringify(model.position)}`, LogCategory.PLAYER);
    Debug.info(`Model rotation: ${JSON.stringify(model.rotation)}`, LogCategory.PLAYER);
    Debug.info(`Model scale: ${JSON.stringify(model.scale)}`, LogCategory.PLAYER);
    Debug.info(`Model visible: ${JSON.stringify(model.visible)}`, LogCategory.PLAYER);
    
    // Check if the model has any children
    Debug.info(`Model children count: ${model.children.length}`, LogCategory.PLAYER);
    model.children.forEach((child, index) => {
      Debug.info(`Child ${index}: ${child.name || 'unnamed'}`, LogCategory.PLAYER);
    });
    
    // Check world matrix
    const worldPosition = new THREE.Vector3();
    model.getWorldPosition(worldPosition);
    Debug.info(`Model world position: ${JSON.stringify(worldPosition)}`, LogCategory.PLAYER);
    
    // Try to fix the model if it's not visible or not moving with player
    Debug.info('Attempting to fix model attachment...', LogCategory.PLAYER);
    
    // Force the model to update its matrix
    model.updateMatrix();
    model.updateMatrixWorld(true);
    
    // Make sure the model is visible
    model.visible = true;
    
    // Make sure the model is at position 0,0,0 relative to player mesh
    model.position.set(0, 0, 0);

    
    Debug.info('Fix attempt complete - check if model is now visible', LogCategory.PLAYER);
  }

  public getDirection(): THREE.Vector3 {
    return this.direction;
  }
  
  public getCharacterMesh(): THREE.Group {
    return this.mesh;
  }

  public getQuaternion(): THREE.Quaternion {
    return this.mesh.quaternion;
  }

  /**
   * Shows the local player ring if it exists
   */
  public showLocalPlayerRing(): void {
    if (this.localPlayerRing) {
      this.localPlayerRing.visible = true;
    }
  }

  /**
   * Hides the local player ring if it exists
   */
  public hideLocalPlayerRing(): void {
    if (this.localPlayerRing) {
      this.localPlayerRing.visible = false;
    }
  }

  /**
   * Debug helper to adjust the ball position in real-time
   * This can be called from the browser console
   */
  public debugAdjustBallPosition(x: number, y: number, z: number): void {
    if (this.ballMesh) {
      this.ballMesh.position.set(x, y, z);
      Debug.info(`Ball position manually adjusted to: (${x.toFixed(2)}, ${y.toFixed(2)}, ${z.toFixed(2)})`, LogCategory.PLAYER);
    } else {
      Debug.warn('Cannot adjust ball position: player does not have the ball', LogCategory.PLAYER);
    }
  }
} 